import { type ReactNode, useEffect } from "react";

import { api } from "../../lib/api";
import { authClient } from "../../lib/auth-client";
import { initPosthog } from "../../lib/posthog";
import { useAuthStore } from "../../stores/authStore";
import { useCreditStore } from "../../stores/creditStore";
import { LoadingScreen } from "../ui/LoadingScreen";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  "http://localhost:8787";

/** Auth hint injected by the Cloudflare Worker into the HTML <head>. */
const authHint = (
  window as unknown as { __AUTH_HINT__?: { hasSession: boolean } }
).__AUTH_HINT__;

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoading, setUser, setLoading, setSignupMode, setIsAdmin } =
    useAuthStore();
  const { data: session, isPending } = authClient.useSession();

  // If the Worker tells us there's no session cookie, skip waiting for
  // the network round-trip — the user is definitely not logged in.
  useEffect(() => {
    if (authHint && !authHint.hasSession && isPending) {
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
    }
  }, [isPending, setUser, setIsAdmin, setLoading]);

  // Fetch public config (signupMode) once on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/config`, { credentials: "include" })
      .then((r) => r.json() as Promise<{ signupMode: string }>)
      .then((data) => {
        if (data.signupMode === "waitlist" || data.signupMode === "open") {
          setSignupMode(data.signupMode);
        }
      })
      .catch(() => {
        // Default to open if config fetch fails
        setSignupMode("open");
      });
  }, [setSignupMode]);

  useEffect(() => {
    if (isPending) {
      // Don't set loading if we already resolved via auth hint
      if (!authHint || authHint.hasSession) {
        setLoading(true);
      }
      return;
    }

    if (session?.user) {
      setUser(session.user);
      void initPosthog().then((ph) =>
        ph?.identify(session.user.id, {
          email: session.user.email,
          name: session.user.name,
        })
      );
      // Fetch credit balance on auth init
      api.credits
        .get()
        .then((balance) => {
          useCreditStore.getState().setBalance(balance);
        })
        .catch(console.error);

      // Check admin status — only clear isLoading after this resolves
      fetch(`${API_BASE}/api/admin/waitlist`, { credentials: "include" })
        .then((r) => setIsAdmin(r.ok))
        .catch(() => setIsAdmin(false))
        .finally(() => setLoading(false));
    } else {
      setUser(null);
      setIsAdmin(false);
      void initPosthog().then((ph) => ph?.reset());
      useCreditStore.getState().clear();
      setLoading(false);
    }
  }, [session, isPending, setUser, setLoading, setIsAdmin]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}
